import { api, setReauthenticator, unwrap } from './client';
import { AppError, isAppError, toAppError } from './errors';
import { sessionFromTokens, sessionStore } from '../auth/session-store';
import { isBackOfficeRole } from '../auth/policy';

/**
 * Single-flight refresh (docs/07-implementation-roadmap.md F1.3), and the one place
 * that decides a session is over (F7.5).
 *
 * **The API rotates the refresh token on every use** (docs/04 §1.1): presenting
 * one revokes it and issues a replacement. Two concurrent refreshes therefore
 * destroy each other — the second presents a token the first has already
 * revoked, gets a 401, and the user is signed out in the middle of whatever they
 * were doing. A dashboard firing five queries in parallel meets this on the
 * first token expiry, which is roughly fifteen minutes into every working day.
 *
 * So there is exactly one refresh in flight at a time and everything else waits
 * on it.
 *
 * ### Not every failed refresh is a finished session (F7.5)
 *
 * This file used to clear the session on any non-OK response, which is right for
 * the 401 it was written for and wrong for everything else. The case that
 * matters is the 429: `API_RATE_LIMIT_MAX` is 100 requests per minute **per IP**
 * and the office shares one address, so the throttle is reachable in ordinary
 * work — and the first request of any page load is `POST /auth/refresh`, which
 * means the throttle lands *here* before it lands anywhere a screen could
 * explain it. Clearing on it signed the whole office out and left them looking
 * at a login form that would 429 too, so the symptom read as "the system is
 * broken" rather than "wait a minute".
 *
 * The rule now: **only the server saying so ends a session.** A 401 or 403 is
 * that answer. A 429, a 5xx or a dead connection is the API declining to answer,
 * and the session is left exactly as it was for the next attempt.
 */

/**
 * What one refresh attempt established.
 *
 * `signedOut` means the store has already been cleared; `unavailable` means it
 * has deliberately not been touched.
 */
export type RefreshOutcome =
  { kind: 'session'; accessToken: string } | { kind: 'signedOut' } | { kind: 'unavailable'; error: AppError };

let inFlight: Promise<RefreshOutcome> | null = null;

function sharedRefresh(): Promise<RefreshOutcome> {
  inFlight ??= runRefresh().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/**
 * The current access token, refreshed if necessary — or `null` when there is
 * none to be had, whether because the session is over or because the API could
 * not be reached.
 *
 * The caller is `client.ts`'s 401 replay, which does the same thing either way:
 * let the original failure through. Which of the two it was matters at boot, and
 * that caller reads `restoreSession`'s outcome instead.
 *
 * Concurrent callers share one network request and one rotation.
 */
export async function refreshSession(): Promise<string | null> {
  const outcome = await sharedRefresh();
  return outcome.kind === 'session' ? outcome.accessToken : null;
}

async function runRefresh(): Promise<RefreshOutcome> {
  let tokens;
  try {
    // No body field: the refresh token is the HttpOnly cookie the browser holds
    // and this code cannot read (docs/04 §1.1). `credentials: 'include'` on the
    // client is what sends it, and the same-origin deployment (docs/07 §1.1) is
    // what keeps `SameSite=Strict` from dropping it.
    tokens = await unwrap(api.POST('/api/v1/auth/refresh', { body: {} }));
  } catch (cause) {
    const error = isAppError(cause) ? cause : toAppError({ cause });

    // The server answered the question: this cookie buys nothing any more.
    if (error.endsSession) {
      sessionStore.clear();
      return { kind: 'signedOut' };
    }

    // It didn't. Throttled, broken or unreachable — the session is untouched and
    // the caller decides what to say about the wait.
    return { kind: 'unavailable', error };
  }

  const session = tokens ? sessionFromTokens(tokens) : null;
  // An unreadable token, or one belonging to a role this app is not for, is
  // no session. `isBackOfficeRole` matters on this path and not only at login:
  // a member whose account is promoted — or demoted — mid-session gets the
  // door, not a half-usable shell (docs/07 §0).
  if (!session || !isBackOfficeRole(session.user.role)) {
    sessionStore.clear();
    return { kind: 'signedOut' };
  }

  sessionStore.set(session);
  return { kind: 'session', accessToken: session.accessToken };
}

/**
 * Boot: ask the refresh cookie whether there is still a session
 * (docs/07-implementation-roadmap.md F1.1, F1.4).
 *
 * The access token is in memory, so a reload — or a second tab, or a link
 * pasted into a new window — starts with nothing. The HttpOnly cookie is what
 * survives, and this is where it is spent. Without it every refresh of the page
 * would be a fresh login, which for an app staff live in all day is not a
 * security posture, just an obstacle.
 *
 * **Three answers, not two** (F7.5). Boot used to settle on `anonymous` whenever
 * it could not produce a session, on the reasoning that a login screen beats a
 * spinner that never resolves. It does — but a login screen is a lie when the
 * API is throttling or down: it invites someone to type a password into a form
 * whose submission is refused by the same limiter, and it discards a session
 * that was never actually over. `unavailable` is the third answer, and
 * `SessionProvider` renders it as what it is, with the wait and a retry.
 *
 * The session store is left at `unknown` in that case, which is accurate: boot
 * has not finished, and nothing downstream may assume otherwise.
 */
export type BootOutcome = { kind: 'session' } | { kind: 'anonymous' } | { kind: 'unavailable'; error: AppError };

export async function restoreSession(): Promise<BootOutcome> {
  const outcome = await sharedRefresh();

  switch (outcome.kind) {
    case 'session':
      return { kind: 'session' };
    case 'unavailable':
      return outcome;
    case 'signedOut':
      return { kind: 'anonymous' };
  }
}

/**
 * Give up on a boot that could not reach the API and fall through to the login
 * screen (F7.5).
 *
 * The retry screen is right by default — there is a live session behind that
 * cookie more often than not — but it must not be a trap. Someone who knows the
 * API is down for the afternoon, or who wants to sign in as somebody else, needs
 * a way past it, and this is it.
 */
export function abandonBootRestore(): void {
  if (sessionStore.getState().status === 'unknown') {
    sessionStore.clear();
  }
}

/** Test-only: drop any in-flight refresh so one test's cannot be awaited by the next. */
export function resetRefreshState(): void {
  inFlight = null;
}

setReauthenticator(refreshSession);
